import { cn } from '@simplywallst/ui-core/next';

import { NarrativeAuthorBase } from '@/components/NarrativeAuthor';
import { toCurrencyTruncated, toTruncated } from '@/utilities/formatting';
import { CompanyIntrinsicDiscountBase } from '@/components/CompanyIntrinsicDiscount';
import { NarrativeSetFairValue } from '../../NarrativeSetFairValue';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { useCallback } from 'react';
import { useSnackbar } from '@/features/snackbar';
import type { ValuationOption } from '../narrativeOptionFragment';
import { useNarrativeSyntheticMetadata } from '../hooks/useNarrativeSyntheticMetadata';
import { NarrativeCardCompactSyntheticLayout } from './NarrativeCardCompactSyntheticLayout';

type Props = {
  valuationOption: ValuationOption;
};

export const NarrativeCardCompactSyntheticPopulated = ({
  valuationOption,
}: Props) => {
  const { type, narrative, fairValue, intrinsicDiscount } = valuationOption;

  const { id: narrativeId, company, isPrimary } = narrative ?? {};
  const { id: companyId, analysisValue, data } = company ?? {};
  const { priceTargetAnalystCount } = analysisValue ?? {};
  const currencyIso = getTradingItemCurrencyISO(data?.currencyInfo);
  const { icon, subtitle, label } = useNarrativeSyntheticMetadata({
    type,
    priceTargetAnalystCount,
    owner: narrative?.owner,
  });
  const { addSnackbar } = useSnackbar();
  const onError = useCallback(() => {
    addSnackbar({
      message: 'Failed to select fair value. Please try again.',
      type: 'negative',
      lifeTime: 10000,
    });
  }, [addSnackbar]);

  if (
    !narrativeId ||
    !companyId ||
    typeof isPrimary !== 'boolean' ||
    (type !== 'ANALYSTS' && type !== 'DCF')
  )
    return null;

  return (
    <NarrativeCardCompactSyntheticLayout>
      <div className="col-span-2 @7xl:col-span-1">
        <NarrativeAuthorBase
          title={label}
          subtitle={subtitle}
          titleClassName={cn('text-xs')}
        >
          {icon}
        </NarrativeAuthorBase>
      </div>

      <div className="grid grid-cols-[max-content_auto] gap-x1_5">
        <div className="w-x0_5 rounded-x0_25 bg-chart-03" />
        <div>
          <div className="text-xl font-strong text-solid">
            {toCurrencyTruncated(fairValue || 0, 2, currencyIso ?? 'XXX')}
          </div>
          <div className="grid grid-cols-[max-content_auto] items-center gap-x0_5 text-tiny">
            <div className="text-soft">Fair Value</div>

            <CompanyIntrinsicDiscountBase discount={intrinsicDiscount} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-[max-content_auto] gap-x1_5">
        <div className="w-x0_5 rounded-x0_25 bg-brand-01" />
        <div>
          {typeof priceTargetAnalystCount === 'number' ? (
            <div className="text-xl font-strong text-solid">
              {toTruncated(priceTargetAnalystCount, 0)}
            </div>
          ) : (
            <div className="text-xl font-strong text-soft">N/A</div>
          )}
          <div className="text-tiny text-soft">Number of Analysts</div>
        </div>
      </div>

      <div className="col-span-2 grid @7xl:col-span-1 @7xl:justify-end">
        <NarrativeSetFairValue
          companyId={companyId}
          narrativeId={narrativeId}
          isPrimary={isPrimary}
          onError={onError}
          valuationType={type}
        />
      </div>
    </NarrativeCardCompactSyntheticLayout>
  );
};
